import { createContext, use, useCallback, useMemo, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { Link, NavigationType, useLocation, useNavigate, useNavigationType, type NavigateOptions, type To } from "react-router";

const RETURN_NAVIGATION_STATE = { isReturn: true };

export function isReturnNavigation(state: unknown) {
    return typeof state === "object" && state !== null && "isReturn" in state && state.isReturn === true;
}

const IN_PLACE_NAVIGATION_STATE = { isInlineSelection: true };

export function isInPlaceNavigation(state: unknown) {
    return typeof state === "object" && state !== null && "isInlineSelection" in state && state.isInlineSelection === true;
}

export function useInPlaceNavigate() {
    const navigate = useNavigate();

    return useCallback((to: To, options?: Omit<NavigateOptions, "replace">) => navigate(to, { ...options, replace: true, state: { ...options?.state, ...IN_PLACE_NAVIGATION_STATE } }), [navigate]);
}

export function useReturnNavigate() {
    const navigate = useNavigate();

    return useCallback((to: To, options?: NavigateOptions) => navigate(to, { ...options, state: { ...options?.state, ...RETURN_NAVIGATION_STATE } }), [navigate]);
}

export function ReturnLink({ state, ...rest }: ComponentProps<typeof Link>) {
    return <Link {...rest} state={{ ...state, ...RETURN_NAVIGATION_STATE }} />;
}

interface TrackedLocation {
    pathname: string;
    previousHref?: string;
}

interface ReturnToContextValue {
    previousHref?: string;
    reportSearch: (pathname: string, search: string) => void;
    reportTransient: (pathname: string) => void;
}

const ReturnToContext = createContext<ReturnToContextValue>({ reportSearch: () => undefined, reportTransient: () => undefined });

export interface ReturnToProviderProps {
    children: ReactNode;
}

export function ReturnToProvider({ children }: ReturnToProviderProps) {
    const { pathname } = useLocation();
    const navigationType = useNavigationType();

    const reportedSearchRef = useRef<{ pathname: string; search: string } | undefined>(undefined);
    const reportSearch = useCallback((reportedPathname: string, search: string) => {
        reportedSearchRef.current = { pathname: reportedPathname, search };
    }, []);

    const transientPathnameRef = useRef<string | undefined>(undefined);
    const reportTransient = useCallback((reportedPathname: string) => {
        transientPathnameRef.current = reportedPathname;
    }, []);

    const [tracked, setTracked] = useState<TrackedLocation>(() => ({ pathname }));

    if (tracked.pathname !== pathname) {
        /* oxlint-disable react/refs -- the reports are read only here, at the moment of a shift, and can't be state: `useReturnToSearch` and `useTransientRoute` file them during a descendant's render, where setting the provider's state is illegal */
        const reported = reportedSearchRef.current;
        const outgoingSearch = reported?.pathname === tracked.pathname ? reported.search : "";

        const isLeavingTransient = transientPathnameRef.current === tracked.pathname;
        /* oxlint-enable react/refs */

        setTracked(current => ({
            pathname,
            previousHref: navigationType === NavigationType.Pop ? undefined : isLeavingTransient ? current.previousHref : `${current.pathname}${outgoingSearch}`
        }));
    }

    const value = useMemo<ReturnToContextValue>(() => ({ previousHref: tracked.previousHref, reportSearch, reportTransient }), [tracked.previousHref, reportSearch, reportTransient]);

    return <ReturnToContext value={value}>{children}</ReturnToContext>;
}

export function useReturnTo() {
    return use(ReturnToContext).previousHref;
}

export function useTransientRoute() {
    const { reportTransient } = use(ReturnToContext);
    const { pathname } = useLocation();

    reportTransient(pathname);
}

export function useReturnToSearch() {
    const { reportSearch } = use(ReturnToContext);
    const { pathname } = useLocation();

    if (globalThis.location?.pathname === pathname) {
        reportSearch(pathname, globalThis.location.search);
    }
}
